import * as z from 'zod'
import { langFallback$Fetch } from '../../lang/utils/langfetch'
import { chargeTypeSchema } from './schemas'
import { ChargeType, ChargeTypesInput } from './types'
import {
  ChargeTypesFetchError,
  ChargeTypesValidationError,
} from './ChargeTypesError'

const DEFAULT_NIGHTS = 2
// Default timeout for charge types requests (30 seconds)
const CHARGE_TYPES_TIMEOUT = 30000

export class CampsiteChargeTypesRepositoryFetch {
  ensureInputHasNights(input: ChargeTypesInput): ChargeTypesInput {
    if (!input.depart && !input.nights) {
      return { ...input, nights: DEFAULT_NIGHTS }
    }
    return input
  }

  async get(input: ChargeTypesInput, langCode: string): Promise<ChargeType[]> {
    const cleanedInput = this.ensureInputHasNights(input)
    const url = this.getUrl(cleanedInput)

    let response: ResponseChargeType[]

    // Step 1: Fetch the data
    try {
      response = await langFallback$Fetch<ResponseChargeType[]>(langCode, url, {
        timeout: CHARGE_TYPES_TIMEOUT,
      })
    } catch (error: any) {
      // Silently return empty array for abort errors - they're intentional cancellations
      if (error?.name === 'AbortError' || error?.message?.includes('aborted')) {
        return []
      }
      // Throw custom fetch error - don't log here, let store handle it
      throw new ChargeTypesFetchError(url, cleanedInput, error)
    }

    // Step 2: Validate and map the response
    try {
      return (response || []).map((chargeType) =>
        this.mapObject(
          responseChargeTypeSchema.parse(chargeType),
          cleanedInput,
        ),
      )
    } catch (error: any) {
      // Throw custom validation error - don't log here, let store handle it
      throw new ChargeTypesValidationError(url, cleanedInput, response, error)
    }
  }

  private getUrl(input: ChargeTypesInput) {
    const queryParams = new URLSearchParams()
    for (const [key, value] of Object.entries(input)) {
      let mappedValue = value
      if (key === 'availability') {
        mappedValue = value ? 1 : 0
      }
      if (mappedValue || (key === 'adults' && mappedValue === 0)) {
        queryParams.append(key, mappedValue.toString())
      }
    }
    return `/_/endpoints/charge-types/?${queryParams.toString()}`
  }

  private mapObject(chargeType: any, input: ChargeTypesInput): ChargeType {
    return chargeTypeSchema.parse({
      ...chargeType,
      balanceDueDate: chargeType.balanceDueDate
        ? chargeType.balanceDueDate
        : undefined,
      refundUntilDate: chargeType.refundUntilDate
        ? chargeType.refundUntilDate
        : undefined,
      isNotifiable: !chargeType.isAvailable && !chargeType.isOvercapacity,
      extras: (chargeType.extras || []).map(({ extra, ...other }) => ({
        ...other,
        ...extra,
        descriptionNeedsTranslation: !!extra.descriptionNeedsTranslation,
      })),
      input,
    })
  }
}

type ResponseChargeType = z.infer<typeof responseChargeTypeSchema>

const moneySchema = z.object({
  amount: z.number(),
  amountMinor: z.number(),
  currency: z.string(),
})

const chargeTypeArrivalTaxSchema = z.object({
  name: z.string(),
  amount: moneySchema,
})

const chargeTypeExtraSchema = z.object({
  extra: z.object({
    compulsory: z.boolean(),
    description: z.string(),
    descriptionNeedsTranslation: z.boolean().optional(),
    id: z.string(),
    isVariable: z.boolean(),
    max: z.number().nullable(),
    name: z.string(),
    pricingPeriod: z.number(),
    pricingType: z.object({
      slug: z.string(),
      name: z.string(),
    }),
    terms: z.string().array().optional().nullable(),
  }),
  price: moneySchema,
  pricingDisplay: z.string(),
  pitchtypeExtra: z
    .object({
      included: z.number().nullable(),
      maximum: z.number().nullable(),
    })
    .optional()
    .nullable(),
})

const offerAppliedSchema = z.object({
  name: z.string(),
  discountPercent: z.string().transform((v) => Number(v)),
})

const quoteSchema = z.object({
  price: moneySchema,
  basePrice: moneySchema,
  preDiscountPrice: moneySchema,
  preDiscountBasePrice: moneySchema,
  preDiscountPriceAdults: moneySchema,
  preDiscountPriceChildren: moneySchema,
  preDiscountPriceInfants: moneySchema,
  adults: z.number(),
  children: z.number(),
  infants: z.number(),
  offersActive: z.boolean(),
  offersApplied: offerAppliedSchema.array(),
})

const chargeTypeStateSchema = z.enum([
  'adults_only',
  'available',
  'disabled',
  'max_capacity',
  'min_nights',
  'unavailable',
])

const chargeTypeTaxSchema = z.object({
  type: z.string(),
  inclusive: z.boolean(),
  rate: z.string().transform((v) => Number(v)),
  amount: moneySchema,
})

const stripePaymentWhenSchema = z.enum([
  'deferred_payment',
  'pitchup_only',
  'immediate_payment',
])

const responseChargeTypeSchema = z.object({
  appliedExtras: z
    .array(
      z.object({
        name: z.string(),
        price: z.object({
          amount: z.number(),
          amount_minor: z.number(),
          currency: z.string(),
        }),
        quantity: z.number(),
      }),
    )
    .nullable(),
  arrivalTaxes: chargeTypeArrivalTaxSchema.array(),
  availAfterMinStay: z.boolean(),
  balanceDueDate: z.string().nullable(),
  chargetypeId: z.string(),
  deposit: moneySchema.nullable(),
  ecommerce: z
    .object({
      items: z
        .array(
          z.object({
            item_id: z.string(),
            item_name: z.string(),
            item_category: z.string(),
            item_category2: z.string().optional(),
            item_category3: z.string().optional(),
            item_category4: z.string().optional(),
            quantity: z.coerce
              .number()
              .refine((n) => Number.isFinite(n) && n >= 0, 'Invalid quantity'),
            price: z.coerce
              .number()
              .refine((n) => Number.isFinite(n) && n >= 0, 'Invalid price'),
          }),
        )
        .catch([]),
    })
    .optional()
    .catch({ items: [] }),
  extras: chargeTypeExtraSchema.array().nullable(),
  isAvailable: z.boolean(),
  isAgeRestricted: z.boolean().optional(),
  isOvercapacity: z.boolean(),
  isOvercapacityMessage: z.string().optional().nullable(),
  dogsExceed: z.boolean().nullable().optional(),
  maxDogs: z.number().nullable().optional(),
  minDays: z.number().nullable(),
  personsIncluded: z.number().optional().nullable(),
  pitchesToSell: z.number(),
  pitchtypeId: z.string(),
  price: moneySchema.nullable(),
  priceExArrivalTaxes: moneySchema.nullable().optional(),
  quote: quoteSchema.nullable(),
  refundUntilDate: z.string().nullable().optional(),
  remainder: moneySchema.nullable(),
  state: chargeTypeStateSchema,
  stripePaymentWhen: stripePaymentWhenSchema,
  taxes: chargeTypeTaxSchema.array(),
})
